import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import type { Session, User } from '@supabase/supabase-js'

/**
 * Auth state seam — what UI components observe about the signed-in user.
 *
 * `session` is the authoritative Supabase session. `user` and
 * `currentWorkspaceId` are denormalised for convenient consumption (so
 * components don't have to dig through nested objects). They're always
 * kept in lockstep with `session` via the setters.
 *
 * State transitions:
 *   - boot:               session=null, isLoading=true
 *   - after hydration:    session=null|Session, isLoading=false
 *   - after signInWithOtp success (link sent, user not yet returned):
 *                          unchanged (the user clicks the email next)
 *   - after callback:     session=Session, currentWorkspaceId loaded
 *   - after signOut:      session=null, currentWorkspaceId=null
 *
 * Repo binding is NOT done here — that lives in `bindReposToSession` and
 * is triggered by `AuthProvider` as a side effect of session changes.
 * This store stays UI-state-only.
 */
interface AuthState {
  session: Session | null
  user: User | null
  /** Loaded after sign-in via a one-shot `workspaces?select=id` query. */
  currentWorkspaceId: string | null
  /** True until the initial `getSession()` round-trip completes. */
  isLoading: boolean
  /** Surfaced in SignInDialog when the magic-link send fails. */
  signInError: string | null
  /** Set to true between "Send link" and confirmation; UI flips to "Check your email". */
  magicLinkSent: boolean
  /**
   * v0.8.46 — Last error from `resolveAndCacheWorkspaceId` after the
   * retry chain has been exhausted. Surfaced in `<CloudSyncError>` so
   * a signed-in user whose workspace query keeps failing sees the
   * actual failure mode (instead of an empty Dashboard with no
   * signal — the silent-fall-to-local-repos failure mode the founder
   * hit on 2026-06-23).
   */
  workspaceResolveError: string | null
  /**
   * v0.8.46 — Distinguishes "we haven't tried yet" from "tried and
   * settled to null." Without this, the CloudSyncError banner can't
   * tell whether to show "resolving…" vs "failed." Flipped to true
   * once the retry chain completes (success OR failure).
   */
  workspaceResolveAttempted: boolean
}

interface AuthActions {
  setSession: (session: Session | null) => void
  setCurrentWorkspaceId: (workspaceId: string | null) => void
  setLoading: (loading: boolean) => void
  setSignInError: (error: string | null) => void
  setMagicLinkSent: (sent: boolean) => void
  setWorkspaceResolveError: (error: string | null) => void
  setWorkspaceResolveAttempted: (attempted: boolean) => void
  reset: () => void
}

const initialState: AuthState = {
  session: null,
  user: null,
  currentWorkspaceId: null,
  isLoading: true,
  signInError: null,
  magicLinkSent: false,
  workspaceResolveError: null,
  workspaceResolveAttempted: false,
}

export const useAuthStore = create<AuthState & AuthActions>()(
  immer((set) => ({
    ...initialState,

    setSession: (session) => {
      set((s) => {
        s.session = session
        s.user = session?.user ?? null
        if (!session) {
          // Signing out clears the workspace cache too — the next sign-in
          // will re-fetch from `workspaces` against the new user.
          s.currentWorkspaceId = null
        }
      })
    },

    setCurrentWorkspaceId: (workspaceId) => {
      set((s) => {
        s.currentWorkspaceId = workspaceId
      })
    },

    setLoading: (loading) => {
      set((s) => {
        s.isLoading = loading
      })
    },

    setSignInError: (error) => {
      set((s) => {
        s.signInError = error
      })
    },

    setMagicLinkSent: (sent) => {
      set((s) => {
        s.magicLinkSent = sent
      })
    },

    setWorkspaceResolveError: (error) => {
      set((s) => {
        s.workspaceResolveError = error
      })
    },

    setWorkspaceResolveAttempted: (attempted) => {
      set((s) => {
        s.workspaceResolveAttempted = attempted
      })
    },

    reset: () => {
      set(() => ({ ...initialState, isLoading: false }))
    },
  }))
)

/** Selector helpers — keep call sites concise and stable across renders. */
export const selectIsAuthenticated = (s: AuthState): boolean => s.session !== null
export const selectUserEmail = (s: AuthState): string | null => s.user?.email ?? null
